import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect } from 'react';
import { CoachService } from '../services/coach';
import {
  type StreamingMessage,
  activeThreadIdAtom,
  addMessageToThreadFamily,
  createNewThreadAtom,
  threadErrorFamily,
  threadListAtom,
  threadLoadingFamily,
  threadMessagesFamily,
  threadScrollPositionFamily,
  threadStreamingMessageFamily,
} from '../store/chat-atoms';
import type { ChatMessage, ChatWidget } from '../types/coach';

export function useCoachChat(threadId?: string) {
  const [activeThreadId, setActiveThreadId] = useAtom(activeThreadIdAtom);
  const threadList = useAtomValue(threadListAtom);
  const createNewThread = useSetAtom(createNewThreadAtom);
  
  // Use provided threadId or active thread
  const currentThreadId = threadId ?? activeThreadId;
  
  // Initialize on first load - ensure we have a valid active thread from the persisted list
  useEffect(() => {
    if (!activeThreadId && threadList.length > 0) {
      // If we have threads but no active thread, set the first one as active
      setActiveThreadId(threadList[0]);
    }
  }, [activeThreadId, threadList, setActiveThreadId]);
  
  // Thread-specific atoms
  const [messages, setMessages] = useAtom(
    currentThreadId ? threadMessagesFamily(currentThreadId) : threadMessagesFamily('default')
  );
  const [isLoading, setIsLoading] = useAtom(
    currentThreadId ? threadLoadingFamily(currentThreadId) : threadLoadingFamily('default')
  );
  const [error, setError] = useAtom(
    currentThreadId ? threadErrorFamily(currentThreadId) : threadErrorFamily('default')
  );
  const [streamingMessage, setStreamingMessage] = useAtom(
    currentThreadId ? threadStreamingMessageFamily(currentThreadId) : threadStreamingMessageFamily('default')
  );
  const [scrollPosition, setScrollPosition] = useAtom(
    currentThreadId ? threadScrollPositionFamily(currentThreadId) : threadScrollPositionFamily('default')
  );
  
  const addMessage = useSetAtom(
    currentThreadId ? addMessageToThreadFamily(currentThreadId) : addMessageToThreadFamily('default')
  );

  // Create a new thread if none exists
  const ensureThread = useCallback(() => {
    if (!currentThreadId) {
      const newThreadId = createNewThread();
      return newThreadId;
    }
    return currentThreadId;
  }, [currentThreadId, createNewThread]);

  // Send message to coach
  const sendMessage = useCallback(async (message: string, userId: string) => {
    const workingThreadId = ensureThread();
    
    try {
      setError(null);
      setIsLoading(true);
      setStreamingMessage(null);

      // Add user message immediately
      const userMessage: ChatMessage = {
        id: `user_${Date.now()}`,
        role: 'user',
        widgets: [{ type: 'text', text: message }],
        timestamp: new Date(),
      };
      addMessage(userMessage);

      // Create streaming message placeholder
      const streamingMessageId = `assistant_${Date.now()}`;
      let currentStreamingMessage: StreamingMessage = {
        id: streamingMessageId,
        role: 'assistant',
        widgets: [],
        timestamp: new Date(),
      };

      // Handle streaming updates
      const handleStreamingUpdate = (update: { thinking?: string; widgets: ChatWidget[] }) => {
        currentStreamingMessage = {
          ...currentStreamingMessage,
          widgets: update.widgets,
          thinking: update.thinking,
        };
        setStreamingMessage(currentStreamingMessage);
      };

      // Send message to coach service
      const response = await CoachService.sendMessage(
        message,
        userId,
        workingThreadId,
        handleStreamingUpdate
      );

      // Add final assistant message
      const assistantMessage: ChatMessage = {
        id: streamingMessageId,
        role: 'assistant',
        widgets: response.widgets,
        timestamp: new Date(),
      };
      addMessage(assistantMessage);

      // Clear streaming message
      setStreamingMessage(null);

    } catch (err) {
      console.error('Failed to send message:', err);
      setError(err instanceof Error ? err.message : 'Failed to send message');
      setStreamingMessage(null);
    } finally {
      setIsLoading(false);
    }
  }, [ensureThread, setError, setIsLoading, setStreamingMessage, addMessage]);

  // Load chat history
  const loadHistory = useCallback(async () => {
    if (!currentThreadId) {return;}

    try {
      setError(null);
      setIsLoading(true);

      const history = await CoachService.getChatHistory(currentThreadId);
      
      // Convert backend history to chat messages
      const convertedMessages: ChatMessage[] = history.map((item: any, index: number) => ({
        id: item.id ?? `msg_${index}`,
        role: item.role ?? 'assistant',
        widgets: item.widgets ?? item.inChatWidgets ?? [{ type: 'text', text: item.content ?? 'No content' }],
        timestamp: item.timestamp ? new Date(item.timestamp) : new Date(),
        internal: item.internal,
        content: item.content,
      }));

      setMessages(convertedMessages);
    } catch (err) {
      console.error('Failed to load chat history:', err);
      setError('Failed to load chat history');
    } finally {
      setIsLoading(false);
    }
  }, [currentThreadId, setError, setIsLoading, setMessages]);

  // Clear messages
  const clearMessages = useCallback(() => {
    setMessages([]);
    setError(null);
    setStreamingMessage(null);
  }, [setMessages, setError, setStreamingMessage]);

  // Switch to a different thread
  const switchThread = useCallback((newThreadId: string) => {
    setActiveThreadId(newThreadId);
  }, [setActiveThreadId]);

  // Save scroll position
  const saveScrollPosition = useCallback((position: number) => {
    setScrollPosition(position);
  }, [setScrollPosition]);

  return {
    // State
    messages,
    isLoading,
    error,
    streamingMessage,
    currentThreadId,
    scrollPosition,
    
    // Actions
    sendMessage,
    loadHistory,
    clearMessages,
    switchThread,
    ensureThread,
    saveScrollPosition,
  };
}